import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { BookOpen, FileText, ClockCounterClockwise, Lock, ChartLineUp, Calculator, Stamp } from '@phosphor-icons/react';

/**
 * Compact accounting quick-access strip — shown at the top of the Dashboard.
 * Surfaces the most-used accounting modules so users don't have to dig the sidebar.
 */
export default function AccountingQuickAccess({ user }) {
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const canViewAccounts = user?.permissions?.accounts?.includes('view');

  const cards = [
    {
      label: 'General Ledger',
      desc: 'Account-level transactions with running balance',
      to: '/dashboard/accounts/ledger',
      icon: BookOpen,
      tint: 'from-emerald-500/15 to-emerald-500/5 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
      show: canViewAccounts,
      testid: 'qa-ledger',
    },
    {
      label: 'Journal Entries',
      desc: 'Manual entries & voucher-posted JEs',
      to: '/dashboard/accounts/journal',
      icon: FileText,
      tint: 'from-blue-500/15 to-blue-500/5 text-blue-700 dark:text-blue-300 border-blue-500/20',
      show: canViewAccounts,
      testid: 'qa-journal',
    },
    {
      label: 'Trial Balance & Reports',
      desc: 'Trial Balance, P&L, Balance Sheet',
      to: '/dashboard/accounts/reports',
      icon: ChartLineUp,
      tint: 'from-violet-500/15 to-violet-500/5 text-violet-700 dark:text-violet-300 border-violet-500/20',
      show: canViewAccounts,
      testid: 'qa-reports',
    },
    {
      label: 'Approvals',
      desc: 'Approve / reject pending transactions',
      to: '/dashboard/approvals',
      icon: Stamp,
      tint: 'from-amber-500/15 to-amber-500/5 text-amber-700 dark:text-amber-300 border-amber-500/20',
      show: true,
      testid: 'qa-approvals',
    },
    {
      label: 'Audit Trail',
      desc: 'Complete change history (admin)',
      to: '/dashboard/accounts/audit-trail',
      icon: ClockCounterClockwise,
      tint: 'from-slate-500/15 to-slate-500/5 text-slate-700 dark:text-slate-300 border-slate-500/20',
      show: isAdmin,
      testid: 'qa-audit',
    },
    {
      label: 'Period Locks',
      desc: 'Close historical periods (admin)',
      to: '/dashboard/accounts/period-locks',
      icon: Lock,
      tint: 'from-rose-500/15 to-rose-500/5 text-rose-700 dark:text-rose-300 border-rose-500/20',
      show: isAdmin,
      testid: 'qa-period-locks',
    },
    {
      label: 'Vouchers',
      desc: 'Cash & bank Pay/Receipt vouchers',
      to: '/dashboard/vouchers',
      icon: Calculator,
      tint: 'from-cyan-500/15 to-cyan-500/5 text-cyan-700 dark:text-cyan-300 border-cyan-500/20',
      show: canViewAccounts,
      testid: 'qa-vouchers',
    },
  ].filter(c => c.show);

  if (!cards.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3"
      data-testid="accounting-quick-access"
    >
      {cards.map(c => (
        <Link key={c.to} to={c.to} data-testid={c.testid}>
          <Card className={`bg-gradient-to-br ${c.tint} hover:scale-[1.02] active:scale-[0.99] transition-transform cursor-pointer border h-full`}>
            <CardContent className="p-3 flex flex-col gap-2 h-full">
              <c.icon size={22} weight="duotone" />
              <div className="font-medium text-xs leading-tight">{c.label}</div>
              <div className="text-[10px] opacity-70 leading-snug line-clamp-2">{c.desc}</div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </motion.div>
  );
}
